import * as React from 'react';
import { ArrowDown, ArrowUp, GripVertical, Plus } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceField,
  WorkspaceInput,
  workspaceFieldClass,
} from '@/components/ui/workspace';
import {
  useArchiveVendorList,
  useCreateVendorList,
  usePatchVendorList,
  useSetVendorListProducts,
  useVendorList,
  useVendorLists,
  type VendorList,
} from '@/lib/api/vendor-lists';
import { cn } from '@/lib/utils/cn';
import {
  Actions,
  Empty,
  InlineForm,
  Linkish,
  Page,
  PageHead,
  Section,
  SectionHead,
} from '../kb-bits';
import { plural } from '../kb-logic';
import { useKb } from '../vendor-knowledge-screen';
import { AddProductsSheet } from './add-products-sheet';

/**
 * Firm settings → Vendor knowledge → Vendor lists (T-0267; E14-design §3):
 * named lists of knowledge-base products, "Municipal ERP" or "Mid-market
 * HCM", that consultants add vendors from on an engagement's Vendor
 * intelligence tab. Every firm user reads them; firm admins and curators
 * create, rename, fill, order and archive them. An archived list leaves the
 * pickers and never changes again.
 *
 * It follows the knowledge base's own settings page: a page of sections,
 * forms opened in place, no overlays except the archive confirmation.
 */

export function VendorListsPage() {
  const { canCurate, navigate } = useKb();
  const [showArchived, setShowArchived] = React.useState(false);
  const lists = useVendorLists(showArchived);
  const create = useCreateVendorList();
  const [adding, setAdding] = React.useState(false);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  const submit = () =>
    create.mutate(
      { name: name.trim(), description: description.trim() || null },
      {
        onSuccess: (list) => {
          setAdding(false);
          setName('');
          setDescription('');
          navigate({ kind: 'list', id: list.id });
        },
      },
    );

  return (
    <Page>
      <PageHead title="Vendor lists" eyebrow="Vendor knowledge">
        Curated lists of products consultants pick vendors from on an engagement's Vendor
        intelligence tab. A product can be on several lists.
      </PageHead>

      <Section label="Lists">
        <SectionHead
          title="Lists"
          count={lists.data?.lists.filter((l) => !l.archived_at).length}
          actions={
            <>
              <Linkish tone="quiet" onClick={() => setShowArchived((v) => !v)}>
                {showArchived ? 'Hide archived' : 'Show archived'}
              </Linkish>
              {canCurate && !adding ? (
                <WorkspaceButton className="ml-2" onClick={() => setAdding(true)}>
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  New list
                </WorkspaceButton>
              ) : null}
            </>
          }
        />
        {adding ? (
          <InlineForm aria-label="New vendor list">
            <ListFields
              name={name}
              description={description}
              onName={setName}
              onDescription={setDescription}
            />
            {create.error ? <ErrorMessage error={create.error} /> : null}
            <Actions>
              <WorkspaceButton
                variant="primary"
                disabled={name.trim().length === 0 || create.isPending}
                onClick={submit}
              >
                Create list
              </WorkspaceButton>
              <WorkspaceButton variant="tertiary" onClick={() => setAdding(false)}>
                Cancel
              </WorkspaceButton>
            </Actions>
          </InlineForm>
        ) : null}
        {lists.isPending ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : lists.error ? (
          <ErrorMessage error={lists.error} />
        ) : lists.data.lists.length === 0 ? (
          <Empty className="mt-3">
            No vendor list yet.
            {canCurate ? ' Make one for the products your team usually weighs together.' : ''}
          </Empty>
        ) : (
          <ul className="m-0 mt-3 list-none p-0" aria-label="Vendor lists">
            {lists.data.lists.map((l) => (
              <li key={l.id} className="border-b border-ink-100 last:border-b-0">
                <button
                  type="button"
                  onClick={() => navigate({ kind: 'list', id: l.id })}
                  className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2.5 text-left hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-amber-600"
                >
                  <span className="font-medium text-ink-900">{l.name}</span>
                  <span className="text-meta text-ink-500">
                    {plural(l.product_count, 'product')}
                  </span>
                  {l.archived_at ? (
                    <StatusChip tone="frozen" size="sm">
                      Archived
                    </StatusChip>
                  ) : null}
                  {l.description ? (
                    <span className="w-full text-meta text-ink-500">{l.description}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </Page>
  );
}

function ListFields({
  name,
  description,
  onName,
  onDescription,
}: {
  name: string;
  description: string;
  onName: (v: string) => void;
  onDescription: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <WorkspaceField label="Name">
        {(id) => (
          <WorkspaceInput
            id={id}
            value={name}
            maxLength={120}
            onChange={(e) => onName(e.target.value)}
            placeholder="Municipal ERP"
          />
        )}
      </WorkspaceField>
      <WorkspaceField label="Description" help="Optional. What the list is for, in a line.">
        {(id, describedBy) => (
          <textarea
            id={id}
            aria-describedby={describedBy}
            value={description}
            maxLength={500}
            rows={2}
            onChange={(e) => onDescription(e.target.value)}
            className={cn(workspaceFieldClass, 'min-h-[76px] resize-y py-2 leading-[20px]')}
          />
        )}
      </WorkspaceField>
    </div>
  );
}

export function VendorListPage({ listId }: { listId: string }) {
  const { canCurate, navigate } = useKb();
  const query = useVendorList(listId);
  if (query.isPending) {
    return (
      <Page>
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      </Page>
    );
  }
  if (query.error) {
    return (
      <Page>
        <ErrorMessage error={query.error} />
        <Actions>
          <Linkish onClick={() => navigate({ kind: 'lists' })}>Back to the vendor lists</Linkish>
        </Actions>
      </Page>
    );
  }
  return <ListDetail key={query.data.id} list={query.data} canCurate={canCurate} />;
}

function ListDetail({ list, canCurate }: { list: VendorList; canCurate: boolean }) {
  const { tree, navigate } = useKb();
  const editable = canCurate && list.archived_at === null;
  const patch = usePatchVendorList(list.id);
  const archive = useArchiveVendorList(list.id);
  const setProducts = useSetVendorListProducts(list.id);
  const [renaming, setRenaming] = React.useState(false);
  const [name, setName] = React.useState(list.name);
  const [description, setDescription] = React.useState(list.description ?? '');
  const [confirmArchive, setConfirmArchive] = React.useState(false);
  const [addingProducts, setAddingProducts] = React.useState(false);
  const [dragFrom, setDragFrom] = React.useState<number | null>(null);

  const ids = list.products.map((p) => p.product_id);
  const save = (next: string[]) => setProducts.mutate(next);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= ids.length || from === to) return;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    save(next);
  };

  return (
    <Page>
      <Linkish tone="quiet" onClick={() => navigate({ kind: 'lists' })}>
        All vendor lists
      </Linkish>
      <div className="mt-3">
        <PageHead
          title={list.name}
          eyebrow="Vendor list"
          aside={
            list.archived_at ? (
              <StatusChip tone="frozen" size="sm">
                Archived
              </StatusChip>
            ) : null
          }
        >
          {list.description ?? undefined}
        </PageHead>
      </div>
      {editable && !renaming ? (
        <Actions>
          <Linkish onClick={() => setRenaming(true)}>Rename</Linkish>
          <Linkish tone="danger" onClick={() => setConfirmArchive(true)}>
            Archive
          </Linkish>
        </Actions>
      ) : null}
      {list.archived_at ? (
        <Empty small className="mt-3">
          This list is archived: it is no longer offered on engagements and cannot change.
        </Empty>
      ) : null}
      {renaming ? (
        <InlineForm aria-label="Rename the list">
          <ListFields
            name={name}
            description={description}
            onName={setName}
            onDescription={setDescription}
          />
          {patch.error ? <ErrorMessage error={patch.error} /> : null}
          <Actions>
            <WorkspaceButton
              variant="primary"
              disabled={name.trim().length === 0 || patch.isPending}
              onClick={() =>
                patch.mutate(
                  { name: name.trim(), description: description.trim() || null },
                  { onSuccess: () => setRenaming(false) },
                )
              }
            >
              Save
            </WorkspaceButton>
            <WorkspaceButton variant="tertiary" onClick={() => setRenaming(false)}>
              Cancel
            </WorkspaceButton>
          </Actions>
        </InlineForm>
      ) : null}

      <Section label="Products">
        <SectionHead
          title="Products"
          count={list.products.length}
          hint={
            editable && list.products.length > 1 ? 'Drag to order, or use the arrows.' : undefined
          }
          actions={
            editable ? (
              <WorkspaceButton onClick={() => setAddingProducts(true)}>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add products
              </WorkspaceButton>
            ) : undefined
          }
        />
        {list.products.length === 0 ? (
          <Empty>
            No product on this list yet.
            {editable ? ' Add products to offer them on engagements.' : ''}
          </Empty>
        ) : (
          <ol className="m-0 list-none p-0" aria-label="Products on this list">
            {list.products.map((p, i) => (
              <li
                key={p.product_id}
                draggable={editable}
                onDragStart={() => setDragFrom(i)}
                onDragOver={(e) => {
                  if (dragFrom !== null) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragFrom !== null) move(dragFrom, i);
                  setDragFrom(null);
                }}
                onDragEnd={() => setDragFrom(null)}
                className={cn(
                  'flex items-center gap-2 border-b border-ink-100 py-2 last:border-b-0',
                  dragFrom === i && 'opacity-50',
                )}
              >
                {editable ? (
                  <GripVertical
                    className="h-3.5 w-3.5 flex-none cursor-grab text-ink-300"
                    aria-hidden="true"
                  />
                ) : null}
                <span className="w-6 flex-none font-mono text-meta tabular-nums text-ink-500">
                  {p.position}
                </span>
                <span className="min-w-0 flex-1">
                  {p.vendor.name.trim().toLowerCase() !== p.name.trim().toLowerCase() ? (
                    <span className="text-meta text-ink-500">{p.vendor.name} · </span>
                  ) : null}
                  <span className="font-medium text-ink-900">{p.name}</span>
                  {p.status !== 'active' ? (
                    <StatusChip tone="frozen" size="sm" className="ml-2">
                      Retired
                    </StatusChip>
                  ) : null}
                </span>
                {editable ? (
                  <span className="flex flex-none items-center gap-1">
                    <WorkspaceButton
                      variant="tertiary"
                      aria-label={`Move ${p.name} up`}
                      disabled={i === 0 || setProducts.isPending}
                      onClick={() => move(i, i - 1)}
                    >
                      <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                    </WorkspaceButton>
                    <WorkspaceButton
                      variant="tertiary"
                      aria-label={`Move ${p.name} down`}
                      disabled={i === list.products.length - 1 || setProducts.isPending}
                      onClick={() => move(i, i + 1)}
                    >
                      <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                    </WorkspaceButton>
                    <Linkish
                      tone="quiet"
                      disabled={setProducts.isPending}
                      aria-label={`Remove ${p.name} from the list`}
                      onClick={() => save(ids.filter((id) => id !== p.product_id))}
                    >
                      Remove
                    </Linkish>
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        )}
        {setProducts.error ? <ErrorMessage error={setProducts.error} /> : null}
      </Section>

      {addingProducts ? (
        <AddProductsSheet
          tree={tree}
          listName={list.name}
          onListIds={ids}
          busy={setProducts.isPending}
          error={setProducts.error}
          onClose={() => setAddingProducts(false)}
          onAdd={(picked) =>
            setProducts.mutate([...ids, ...picked], {
              onSuccess: () => setAddingProducts(false),
            })
          }
        />
      ) : null}

      <WorkspaceDialog
        open={confirmArchive}
        onOpenChange={setConfirmArchive}
        title={`Archive ${list.name}?`}
        description="It is no longer offered on engagements, and it cannot change again. Engagements that added vendors from it keep them."
        actions={
          <>
            <WorkspaceButton variant="tertiary" size="md" onClick={() => setConfirmArchive(false)}>
              Cancel
            </WorkspaceButton>
            <WorkspaceButton
              variant="destructive"
              size="md"
              disabled={archive.isPending}
              onClick={() =>
                archive.mutate(undefined, { onSuccess: () => setConfirmArchive(false) })
              }
            >
              Archive list
            </WorkspaceButton>
          </>
        }
      >
        {archive.error ? <ErrorMessage error={archive.error} /> : <span />}
      </WorkspaceDialog>
    </Page>
  );
}
